import { queryOptions } from '@tanstack/react-query';

import { fetchSurveysGateway } from './fetchSurveysGateway';
import { mySurveyResponsesKey } from '../model/surveyQueryKeys';
import type { MySurveyResponse } from '../model/types';
import type { SurveysGateway } from '../ports/surveysGateway';

import { components, paths } from '@/types/api';
import { ApiError, fetchClient, isApiErrorV2 } from '@/util/fetchClient';
import { userScoped } from '@/util/userScope';

export type SurveyCompletion = components['schemas']['SurveyCompletion'];

export type SurveyResponse = NonNullable<
  paths['/v2/surveys/responses/me']['get']['responses']['200']['content']['application/json']['data']
>[number];

export type SurveyDefinition = NonNullable<
  paths['/v2/surveys/{groupIdOrSlug}/current']['get']['responses']['200']['content']['application/json']['data']
>;

/**
 * Whether a survey may only be answered by a signed-in user, so the response
 * attaches to an account rather than to a guest-supplied email.
 *
 * Stays tolerant of a survey that predates the field, and of a wire value that
 * is truthy without being the boolean — anonymous answering is the permissive
 * default, so only an explicit `true` closes it off.
 */
export function surveyRequiresAuthentication(
  survey: Partial<Pick<SurveyDefinition, 'requiresAuthentication'>> | null | undefined
): boolean {
  return survey?.requiresAuthentication === true;
}

/**
 * Whether a failed public survey fetch means "sign in first" rather than
 * "this survey is gone". The public endpoint refuses an auth-required survey
 * outright instead of returning its definition, so an anonymous client never
 * sees `requiresAuthentication` and the status code is the only signal it
 * gets. Anything else — a missing group, an unpublished version, a server
 * error — is a genuine unavailability and must not route a visitor to a
 * sign-in screen that won't help them.
 */
export function isSurveyAuthRequiredError(error: unknown): boolean {
  if (!(error instanceof ApiError) && !isApiErrorV2(error)) {
    return false;
  }

  return error.status === 401 || error.status === 403;
}

/**
 * Resolves a survey group to whichever version is currently published, so
 * callers can hold a stable group id (e.g. an env var) instead of a
 * version-specific definition id that goes stale every time a new version
 * is published.
 */
export const getCurrentSurveyByGroupQueryOptions = (
  surveyGroupId: string,
  gateway: SurveysGateway = fetchSurveysGateway
) =>
  queryOptions({
    queryKey: ['surveyV2Current', surveyGroupId],
    queryFn: () => gateway.getCurrentSurvey(surveyGroupId),
    enabled: !!surveyGroupId,
  });

export const getMySurveyResponsesV2QueryOptions = userScoped(
  'user',
  (userId: string | undefined, gateway: SurveysGateway = fetchSurveysGateway) =>
    queryOptions({
      queryKey: mySurveyResponsesKey(userId),
      queryFn: () => gateway.listMyResponses(),
    })
);

export async function submitSurveyResponse(surveyId: string, answers: Record<string, unknown>) {
  const { data } = await fetchClient.POST('/v2/surveys/{id}/responses', {
    params: { path: { id: surveyId } },
    body: { answers },
  });

  return data?.data;
}

export function hasRespondedToSurvey(responses: MySurveyResponse[], surveyId: string): boolean {
  return responses.some((response) => response.surveyDefinitionId === surveyId);
}

export const getPublicSurveyByGroupQueryOptions = (
  surveyGroupId: string,
  gateway: SurveysGateway = fetchSurveysGateway
) =>
  queryOptions({
    queryKey: ['publicSurveyByGroupV2', surveyGroupId],
    queryFn: () => gateway.getPublicSurvey(surveyGroupId),
    enabled: !!surveyGroupId,
  });
